import { useMemo } from 'react';
import { Button } from '@tempmee/react';
import { OutlineChevronLeftIcon } from '@tempmee/react-icons';
import { Column, Text } from '~/commonComponents';
import { Colors } from '~/themes/colors';
import { TEducationMap } from '~/domain/FavoriteProfessionals/favoriteProfessionalsTypes';
import { EducationUtils } from '~/utils/EducationUtils';
import { UserProfession } from '~/enums/UserProfession';

interface IViewSkillsScreenProps {
  fullName: string;
  profession: string;
  professionCode: string;
  education: TEducationMap;
  onBack: () => void;
}

const isDnDa = (code: string) => [UserProfession.DN, UserProfession.DA].includes(code);

const isRdhFd = (code: string) => [UserProfession.RDH, UserProfession.FD].includes(code);

export const ViewSkillsScreen = ({
  fullName,
  profession,
  professionCode,
  education,
  onBack,
}: IViewSkillsScreenProps) => {
  // Try exact professionCode first, then fall back to first available entry
  const resolvedCode = education[professionCode]
    ? professionCode
    : (Object.keys(education)[0] ?? professionCode);
  const educationEntry = education[resolvedCode];
  const specialtiesAndProcedures = useMemo(
    () => educationEntry?.specialtiesAndProcedures ?? {},
    [educationEntry],
  );

  // DN/DA: specialty keys map to procedure arrays
  const dnDaSpecialties = useMemo(() => {
    if (!isDnDa(resolvedCode)) return [];
    return Object.entries(specialtiesAndProcedures);
  }, [resolvedCode, specialtiesAndProcedures]);

  // RDH/FD: procedures are top-level keys prefixed with "procedure_"
  const rdhFdProcedures = useMemo(() => {
    if (!isRdhFd(resolvedCode)) return [];
    return EducationUtils.getProceduresOfProfession({
      specialtiesAndProcedures,
      profession: resolvedCode,
      specialties: [],
    }) as string[];
  }, [resolvedCode, specialtiesAndProcedures]);

  return (
    <Column flex={1} position="relative">
      <Button
        variant="ghost"
        size="inline"
        onClick={onBack}
        LeadingIcon={<OutlineChevronLeftIcon />}
        style={{ position: 'absolute', left: 24, top: 24, zIndex: 1 }}
      >
        Back
      </Button>

      <Column marginBottom={24} marginTop={54}>
        <Text bold fontSize={18} textAlign="center">
          Professional Skills
        </Text>
      </Column>

      <Column paddingHorizontal={48} marginBottom={16}>
        <Text fontSize={20}>
          <Text bold fontSize={20} color={Colors.neutral_500}>
            {fullName}
          </Text>
          <Text fontSize={18} color={Colors.neutral_500}>
            {` - ${profession}`}
          </Text>
        </Text>
      </Column>

      <Column
        flex={1}
        flexBasis={0}
        overflowY="auto"
        paddingHorizontal={48}
        style={{ scrollbarWidth: 'thin' }}
      >
        {isDnDa(resolvedCode) &&
          dnDaSpecialties.map(([specialtyKey, procedures]) => (
            <Column key={specialtyKey} marginBottom={16}>
              <Text bold fontSize={18} color={Colors.primary_500}>
                {EducationUtils.removeSpecialtyPrefix(specialtyKey)}
              </Text>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                  marginTop: 8,
                }}
              >
                {procedures.map((proc: string) => (
                  <Text key={proc} fontSize={16} color={Colors.neutral_600}>
                    {`\u2022  ${EducationUtils.removeProcedurePrefix(proc)}`}
                  </Text>
                ))}
              </div>
            </Column>
          ))}

        {isRdhFd(resolvedCode) && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            {rdhFdProcedures.map((name: string) => (
              <Text key={name} fontSize={16} color={Colors.neutral_600}>
                {`\u2022  ${EducationUtils.removeProcedurePrefix(name)}`}
              </Text>
            ))}
          </div>
        )}
      </Column>
    </Column>
  );
};
